import React from "react";
import { Activity, Clock, Compass, Crosshair, MapPin, Navigation, Target } from "lucide-react";
import { distanceNm, formatCoord, routeDistance } from "./navigationRebuildUtils";

const EARTH_RADIUS_NM = 3440.065;

function toRad(value) {
  return (value * Math.PI) / 180;
}

function toDeg(value) {
  return (value * 180) / Math.PI;
}

function bearingDegrees(from, to) {
  if (!from || !to) return null;
  const lat1 = toRad(from[0]);
  const lat2 = toRad(to[0]);
  const dLng = toRad(to[1] - from[1]);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function crossTrackErrorNm(position, routePoints) {
  if (!position || routePoints.length < 2) return null;
  const start = routePoints[0];
  const end = routePoints[1];
  const d13 = distanceNm(start, position) / EARTH_RADIUS_NM;
  const b13 = toRad(bearingDegrees(start, position) || 0);
  const b12 = toRad(bearingDegrees(start, end) || 0);
  return Math.abs(Math.asin(Math.sin(d13) * Math.sin(b13 - b12)) * EARTH_RADIUS_NM);
}

function ReadoutCard({ icon: Icon, label, value, sub, tooltip, mobileHidden = false }) {
  return (
    <div
      title={tooltip || sub || label}
      aria-label={tooltip || sub || label}
      className={`${mobileHidden ? "hidden md:block" : ""} group relative min-w-0 overflow-hidden rounded-xl border border-cyan-200/35 bg-slate-950/78 px-2 py-1.5 shadow-lg shadow-cyan-950/20 backdrop-blur-md ring-1 ring-white/10 dark:border-cyan-200/35 dark:bg-slate-950/82 dark:ring-cyan-100/15 md:rounded-2xl md:bg-slate-950/94 md:px-3 md:py-2 md:backdrop-blur-xl`}
    >
      <div className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-cyan-300/80 md:inset-y-2 md:w-1" />
      <div className="flex min-w-0 items-center gap-1 text-[8px] font-black uppercase leading-none tracking-[0.08em] text-cyan-100/80 md:gap-1.5 md:text-[10px] md:tracking-[0.13em]">
        <Icon className="h-3 w-3 shrink-0 text-cyan-300 md:h-3.5 md:w-3.5" /> <span className="truncate">{label}</span>
      </div>
      <p className="mt-1 truncate text-[13px] font-black leading-tight text-white md:mt-1 md:text-lg md:leading-none">{value}</p>
      {sub && <p className="mt-0.5 truncate text-[8px] font-semibold leading-tight text-cyan-100/70 md:mt-1 md:text-[11px]">{sub}</p>}
    </div>
  );
}

export default function NavigationReadoutCards({ telemetry, routePoints }) {
  const position = telemetry?.point;
  const activeWaypoint = routePoints[0];
  const destination = routePoints[routePoints.length - 1];
  const sog = telemetry?.speedKnots || 0;
  const cog = Number.isFinite(telemetry?.heading) ? telemetry.heading : null;
  const bearing = bearingDegrees(position, activeWaypoint);
  const range = position && activeWaypoint ? distanceNm(position, activeWaypoint) : 0;
  const milesToDestination = position && destination ? distanceNm(position, activeWaypoint) + routeDistance(routePoints) : routeDistance(routePoints);
  const etaSpeed = sog > 0.5 ? sog : 6;
  const eta = milesToDestination ? milesToDestination / etaSpeed : 0;
  const xte = crossTrackErrorNm(position, routePoints);

  return (
    <div className="pointer-events-none absolute bottom-[5.15rem] left-2 right-2 z-[850] md:bottom-auto md:left-1/2 md:right-auto md:top-3 md:w-[min(48rem,calc(100vw-14rem))] md:-translate-x-1/2">
      <div className="pointer-events-auto grid grid-cols-3 gap-1.5 rounded-2xl border border-white/10 bg-slate-950/18 p-1.5 shadow-xl shadow-black/15 backdrop-blur-sm dark:border-cyan-100/15 dark:bg-slate-950/28 md:grid-cols-4 md:gap-2 md:bg-transparent md:p-0 md:shadow-none md:backdrop-blur-none dark:md:bg-transparent">
        <ReadoutCard icon={MapPin} label={telemetry?.isEstimated ? "Est. pos" : "Position"} value={position ? formatCoord(position[0], "lat") : "GPS wait"} sub={position ? formatCoord(position[1], "lng") : "Allow location access"} tooltip="Current vessel position from GPS, or estimated from recent movement when signal is stale." />
        <ReadoutCard icon={Activity} label="SOG" value={`${sog.toFixed(1)} kn`} sub="Speed over ground" tooltip="Your current movement speed over the ground in knots." />
        <ReadoutCard icon={Compass} label="COG" value={cog === null ? "—" : `${Math.round(cog)}°`} sub="Course over ground" tooltip="The direction your vessel is moving over the ground." />
        <ReadoutCard icon={Navigation} label="BRG" value={bearing === null ? "—" : `${Math.round(bearing)}°`} sub="To active waypoint" tooltip="Direction from your vessel to the next active waypoint." />
        <ReadoutCard mobileHidden icon={Crosshair} label="Range" value={activeWaypoint ? `${range.toFixed(2)} NM` : "—"} sub="To active waypoint" tooltip="Distance from your vessel to the next active waypoint." />
        <ReadoutCard icon={Target} label="XTE" value={xte === null ? "—" : `${xte.toFixed(2)} NM`} sub="Cross-track error" tooltip="How far your vessel is from the planned route line." />
        <ReadoutCard mobileHidden icon={Target} label="To dest" value={destination ? `${milesToDestination.toFixed(2)} NM` : "—"} sub="Total remaining" tooltip="Total remaining nautical miles to the final destination." />
        <ReadoutCard icon={Clock} label="ETA" value={eta ? `${eta.toFixed(1)}h` : "—"} sub={sog > 0.5 ? "Based on SOG" : "Planned @ 6kn"} tooltip="Estimated time to destination based on your current speed or planned speed." />
      </div>
    </div>
  );
}